import {
  Capabilities,
  DocType,
  ItemType,
  PermissionName,
} from "../../../clients/graphqlTypes.js";

export type CreateItemInputAllowances = {
  areAnyTemplatesAllowed: boolean;
  areAnyProductsAllowed: boolean;
  allowTemplatesSearch: boolean;
  allowTemplatesInDropdown: boolean;
  allowProductsSearch: boolean;
  allowProductsInDropdown: boolean;
  allowProductListsSearch: boolean;
  allowProductListsInDropdown: boolean;
  allowRecommendations: boolean;
};

/**
 * Computes which entry points the create-item combobox offers for the given
 * document/item context and viewer rights (templates, products, and
 * recommended templates).
 */
export function getCreateItemInputAllowances({
  docType,
  itemType,
  documentItemTypes,
  viewerCapabilities,
  viewerPermissions,
  hasRecommendationHandlers,
}: {
  docType: DocType;
  itemType: ItemType;
  documentItemTypes: ItemType[];
  viewerCapabilities: Capabilities[];
  viewerPermissions: PermissionName[];
  /**
   * Whether both `fetchItemRecommendations` and `createItemFromRecommendation`
   * were injected. Only quote consumers inject them (see useCreateQuoteItems).
   */
  hasRecommendationHandlers: boolean;
}): CreateItemInputAllowances {
  const areAnyTemplatesAllowed =
    ["section", "decision"].includes(itemType) &&
    documentItemTypes.includes("section") &&
    viewerCapabilities.includes("templating") &&
    viewerPermissions.includes("template_usage_create");

  const areAnyProductsAllowed =
    ["section", "decision"].includes(itemType) &&
    documentItemTypes.includes("paid") &&
    viewerCapabilities.includes("pim");

  const allowTemplatesSearch =
    (docType === "REQUIREMENT" ||
      docType === "QUOTE" ||
      docType === "TEMPLATE") &&
    areAnyTemplatesAllowed;

  const allowTemplatesInDropdown =
    (docType === "REQUIREMENT" ||
      docType === "QUOTE" ||
      docType === "TEMPLATE" ||
      docType === "BUILDING") &&
    areAnyTemplatesAllowed;

  const allowProductsSearch =
    (docType === "QUOTE" || docType === "TEMPLATE") && areAnyProductsAllowed;

  const allowProductsInDropdown =
    (docType === "QUOTE" || docType === "TEMPLATE") && areAnyProductsAllowed;

  // The product-list queries require `pim_catalogue_update` on top of the `pim`
  // capability that plain product search needs, so they are gated separately —
  // otherwise a quote editor without that permission is offered an entry point
  // that can only return `MissingPermissions`.
  const areAnyProductListsAllowed =
    areAnyProductsAllowed && viewerPermissions.includes("pim_catalogue_update");

  const allowProductListsSearch =
    (docType === "QUOTE" || docType === "TEMPLATE") &&
    areAnyProductListsAllowed;

  const allowProductListsInDropdown = allowProductListsSearch;

  // Recommendations are quote-only: only quote consumers inject the handlers.
  const allowRecommendations =
    docType === "QUOTE" && areAnyTemplatesAllowed && hasRecommendationHandlers;

  return {
    areAnyTemplatesAllowed,
    areAnyProductsAllowed,
    allowTemplatesSearch,
    allowTemplatesInDropdown,
    allowProductsSearch,
    allowProductsInDropdown,
    allowProductListsSearch,
    allowProductListsInDropdown,
    allowRecommendations,
  };
}
